import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import uavplatform1 from '../assets/uavplatform1.png';
import uavplatform2 from '../assets/uavplatform2.png';
import uavplatform3 from '../assets/uavplatform3.png';

const UAVPlatform = () => {
  const { t } = useTranslation();
  const [activeImage, setActiveImage] = useState(0);

  // Auto-rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % 3);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const uavProduct = {
    name: t('uav.product.name'),
    tagline: t('uav.product.tagline'),
    description: t('uav.product.description'),
    images: [
      uavplatform1,
      uavplatform2,
      uavplatform3
    ],
    specs: {
      [`🕑 ${t('uav.specs.flightDuration')}`]: '120 minutes',
      [`📦 ${t('uav.specs.payloadCapacity')}`]: '2000 g',
      [`⚡ ${t('uav.specs.motorType')}`]: 'Electric',
      [`📏 ${t('uav.specs.wingspan')}`]: 'Maximum 168 cm',
      [`🧱 ${t('uav.specs.bodyStructure')}`]: 'Carbon Fiber Composite, Modular',
      [`🚁 ${t('uav.specs.verticalTakeoff')}`]: '3 motors, 2 with tilt mechanism',
      [`🛫 ${t('uav.specs.maxFlightRange')}`]: '50 km',
      [`📶 ${t('uav.specs.maxLinkDistance')}`]: '50 km (LOS)',
      [`💨 ${t('uav.specs.maxFlightSpeed')}`]: '15 m/s'
    }
  };

  const applications = [
    {
      icon: '🎖️',
      title: t('uav.applications.military.title'),
      description: t('uav.applications.military.description')
    },
    {
      icon: '🔍',
      title: t('uav.applications.intelligence.title'),
      description: t('uav.applications.intelligence.description')
    },
    {
      icon: '🛡️',
      title: t('uav.applications.border.title'),
      description: t('uav.applications.border.description')
    },
    {
      icon: '🚨',
      title: t('uav.applications.emergency.title'),
      description: t('uav.applications.emergency.description')
    }
  ];

  return (
    <div className="pt-16 md:pt-20">
      {/* Military Hero Section */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-gradient-military"
          >
            {t('uav.page.title')}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent mx-auto"
          />
        </div>
      </section>

      {/* Product Showcase */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <div className="max-w-6xl mx-auto">
            {/* Product Images */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative h-96 md:h-[500px] mb-12 rounded-lg overflow-hidden"
            >
              {uavProduct.images.map((image, index) => (
                <motion.div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    activeImage === index ? 'opacity-100' : 'opacity-0'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: activeImage === index ? 1 : 0 }}
                  transition={{ duration: 1 }}
                >
                  <img
                    src={image}
                    alt={`${uavProduct.name} ${index + 1}`}
                    className="w-full h-full object-contain bg-black/20"
                  />
                  <div className="absolute inset-0 bg-black/30"></div>
                </motion.div>
              ))}
              
              {/* Image Indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {uavProduct.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      activeImage === index 
                        ? 'bg-military-accent scale-125' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
                {uavProduct.name}
              </h2>
              <p className="text-xl md:text-2xl text-military-accent font-bold mb-6">
                {uavProduct.tagline}
              </p>
              <p className="text-lg md:text-xl text-military-silver leading-relaxed max-w-4xl mx-auto font-military-mono">
                {uavProduct.description}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Specifications Section */}
      <section className="section-military bg-black">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
              {t('uav.specs.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono">
              {t('uav.specs.subtitle')}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {Object.entries(uavProduct.specs).map(([key, value], index) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="military-card p-6 md:p-8"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-2xl mr-4">{key.split(' ')[0]}</span>
                      <span className="font-bold text-military-accent font-military-mono text-sm md:text-base">
                        {key.split(' ').slice(1).join(' ')}
                      </span>
                    </div>
                    <span className="text-lg font-black text-gradient-military">{value}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
              {t('uav.applications.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono">
              {t('uav.applications.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {applications.map((app, index) => (
              <motion.div
                key={app.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="military-card text-center p-6 md:p-8"
              >
                <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">{app.icon}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                  {app.title}
                </h3>
                <p className="text-military-silver font-military-mono text-sm md:text-base">
                  {app.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-8">
              {t('uav.cta.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver mb-12 max-w-2xl mx-auto font-military-mono">
              {t('uav.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="/contact" className="btn-military-primary">
                {t('uav.cta.contact')}
              </a>
              <a href="/contact" className="btn-military-secondary">
                {t('uav.cta.quote')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UAVPlatform;
